import type {DesignerStateType} from '@packages/systems/core/records/DesignerState';
import type {
  PageRecordType,
  PageState,
} from '@packages/systems/core/records/PageRecord';
import {qualify} from '@packages/systems/wfdl-Qualified';
import {Component, Plugins} from '@packages/systems/wfdl';

/**
 * When a user is updating their design, a staged version of their design
 * is created. This is called the "live" component map because it is what is reflected
 * in the Designer. If the user "confirms" their change, by dropping the element or
 * by a Data Type transaction completely, the staged component map will become the
 * next persisted component map.
 *
 * Generally, if you are reading from the component map you'll want to use this function
 * to select the current component map that the user is working on and seeing.
 *
 * Inlined here to avoid circular dependency with @packages/utilities/platform-selectors
 */
const getLiveComponentMap = (
  designerState: DesignerStateType
): Component.ComponentMap => {
  return designerState.stagedComponents || designerState.components;
};

/**
 * Returns the currently set Component Page, which is set when the Component Canvas is active
 */
// Safe since we are not creating new objects
// eslint-disable-next-line webflow/selector-memoization
export const selectCurrentComponentCanvasPage = ({
  DesignerStore,
  PageStore,
}: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}): undefined | PageRecordType =>
  PageStore.get('componentPages').get(DesignerStore.currentPageId);

export const getComponentPageComponentId = (page: PageRecordType) =>
  page.pageKind?.meta.componentId;

const getComponentCanvasComponent = (
  designerStore: DesignerStateType,
  page: PageRecordType
) => {
  const componentId = getComponentPageComponentId(page);
  return componentId
    ? getLiveComponentMap(designerStore).get(
        qualify(Plugins.name('__SitePlugin'), Component.name(componentId))
      )
    : undefined;
};

export const isCodeComponentPage = (
  designerStore: DesignerStateType,
  page: PageRecordType
) => {
  const component = getComponentCanvasComponent(designerStore, page);
  return Boolean(component && Component.getSource(component));
};

/**
 * Returns `true` if the current component in the Component Canvas is a code component.
 */
export const selectIsCodeComponentCanvas = (state: {
  DesignerStore: DesignerStateType;
  PageStore: PageState;
}) => {
  const page = selectCurrentComponentCanvasPage(state);
  return page ? isCodeComponentPage(state.DesignerStore, page) : false;
};
